import React from "react";
import { MapPin, Truck } from "lucide-react";
import { useT } from "@/lib/i18n";
import { Button } from "@/components/ui/button";
import { BUSINESS } from "@/lib/api";

const CITIES = [
  {
    name: "Chandler",
    distance: "Home base",
    zips: "85225 · 85226 · 85248 · 85249 · 85286",
    days: "Tue · Wed · Thu · Sat",
    featured: true,
  },
  {
    name: "Ahwatukee",
    distance: "~ 8 miles",
    zips: "85044 · 85045 · 85048",
    days: "Tue · Wed · Thu · Sat",
  },
  {
    name: "Tempe",
    distance: "~ 10 miles",
    zips: "85281 · 85282 · 85283 · 85284",
    days: "Tue · Wed · Thu · Sat",
  },
  {
    name: "Gilbert",
    distance: "~ 6 miles",
    zips: "85233 · 85234 · 85295 · 85296 · 85297",
    days: "Tue · Wed · Thu · Sat",
  },
  {
    name: "Maricopa",
    distance: "~ 24 miles",
    zips: "85138 · 85139",
    days: "Mon · Thu",
  },
];

export default function CoverageMap() {
  const { t } = useT();

  return (
    <section
      id="coverage"
      data-testid="coverage-section"
      className="py-20 lg:py-28 bg-gradient-to-b from-slate-50 to-white"
    >
      <div className="max-w-7xl mx-auto px-5 sm:px-8">
        <div className="flex flex-col lg:flex-row lg:items-end lg:justify-between gap-6 mb-12">
          <div>
            <span className="inline-flex items-center gap-1.5 rounded-full bg-brand-lightblue text-brand-blueDark px-3 py-1 text-[11px] font-bold uppercase tracking-[0.18em]">
              <Truck className="h-3 w-3" /> {t("coverage.tag")}
            </span>
            <h2 className="mt-4 font-display text-3xl sm:text-4xl lg:text-5xl font-semibold tracking-tight text-brand-navy max-w-2xl">
              {t("coverage.title")}
            </h2>
          </div>
          <p className="text-slate-600 max-w-md leading-relaxed">
            {t("coverage.body")}
            <span className="block mt-2 text-sm font-semibold text-brand-navy">
              ⏱ 48-hour turnaround on most orders.
            </span>
          </p>
        </div>

        <div className="grid lg:grid-cols-12 gap-6">
          {/* Map */}
          <div className="lg:col-span-7 relative rounded-3xl overflow-hidden border border-slate-200 shadow-sm bg-white min-h-[440px]">
            <iframe
              data-testid="coverage-map-iframe"
              title="Oxford Cleaners service area"
              src="https://www.google.com/maps?q=Chandler+AZ+85226&z=10&output=embed"
              className="absolute inset-0 w-full h-full"
              loading="lazy"
              referrerPolicy="no-referrer-when-downgrade"
            />
            {/* Floating shop card */}
            <div className="absolute top-4 left-4 sm:top-5 sm:left-5 rounded-2xl bg-white/95 backdrop-blur-md shadow-xl border border-slate-100 px-4 py-3 max-w-[260px] z-10 pointer-events-none">
              <div className="flex items-center gap-2">
                <span className="h-7 w-7 rounded-full bg-brand-blue text-white grid place-items-center font-display font-bold text-sm">
                  O
                </span>
                <div>
                  <p className="font-display text-sm font-bold text-brand-navy leading-tight">
                    Oxford Cleaners
                  </p>
                  <p className="text-[11px] text-slate-500">{BUSINESS.address}</p>
                </div>
              </div>
            </div>
          </div>

          {/* Cities list */}
          <div className="lg:col-span-5 grid gap-3">
            {CITIES.map((c) => (
              <div
                key={c.name}
                data-testid={`coverage-city-${c.name.toLowerCase()}`}
                className={`lift rounded-2xl border p-5 flex items-start gap-4 transition-colors ${
                  c.featured
                    ? "border-brand-blue/50 bg-brand-lightblue/40"
                    : "border-slate-200 bg-white"
                }`}
              >
                <div
                  className={`h-11 w-11 shrink-0 rounded-xl grid place-items-center ${
                    c.featured
                      ? "bg-brand-blue text-white"
                      : "bg-brand-lightblue text-brand-blue"
                  }`}
                >
                  <MapPin className="h-5 w-5" />
                </div>
                <div className="flex-1 min-w-0">
                  <div className="flex items-baseline justify-between gap-3">
                    <h3 className="font-display text-lg font-bold text-brand-navy">
                      {c.name}, AZ
                    </h3>
                    <span className="text-[11px] font-semibold uppercase tracking-wider text-slate-500 shrink-0">
                      {c.distance}
                    </span>
                  </div>
                  <p className="mt-1 text-[11px] font-semibold uppercase tracking-[0.14em] text-slate-400">
                    {c.days || t("coverage.cityNote")}
                  </p>
                  <p className="mt-1 text-sm text-slate-600 break-words">
                    {c.zips}
                  </p>
                </div>
              </div>
            ))}
            <Button
              asChild
              data-testid="coverage-call-cta"
              className="mt-1 rounded-full bg-brand-navy hover:bg-brand-blue text-white h-11 px-6 self-start"
            >
              <a href={`tel:${BUSINESS.phoneTel}`}>{t("coverage.checkArea")}</a>
            </Button>
          </div>
        </div>
      </div>
    </section>
  );
}
